import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Crown, ArrowRight, Check, AlertTriangle, Sparkles, Zap } from 'lucide-react';

const MARKETPLACE_LINK = 'https://buy.stripe.com/cNidRbfENgRR113fSk8AE00';
const CREATOR_LINK = 'https://buy.stripe.com/eVq7sN3W51WX5hj0Xq8AE01';
const STUDIO_LINK = 'https://buy.stripe.com/9B600l2S1dFF9xz49C8AE02';

export default function SubscriptionWall({ user }) {
  const status = user?.subscription_status;
  const trialEnd = user?.trial_end_date;
  const plan = user?.subscription_plan;
  const subEnd = user?.subscription_end_date;

  // Format a date string nicely
  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-rose-100 flex items-center justify-center mb-6">
          <AlertTriangle size={36} className="text-amber-600" />
        </div>

        <h1 className="font-serif text-2xl md:text-3xl text-obsidian mb-3" data-testid="subscription-wall-title">
          Your free trial has ended
        </h1>
        <p className="text-muted-foreground font-light mb-8 text-sm leading-relaxed">
          {trialEnd
            ? `Your 30-day free trial ended on ${formatDate(trialEnd)}.`
            : 'Your free trial period has expired.'}
          {' '}Subscribe to continue creating and selling on PushPro.
        </p>

        {/* Plans Quick View */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <a
            href={`${MARKETPLACE_LINK}?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.email || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-amber-200 rounded-xl p-4 text-left hover:border-amber-400 transition-colors block bg-amber-50/30"
          >
            <Crown size={14} className="text-amber-500 mb-1" />
            <p className="text-[10px] text-amber-600 font-semibold uppercase tracking-wider mb-1">Marketplace</p>
            <p className="font-serif text-xl text-obsidian">{'\u20AC'}29<span className="text-[10px] text-muted-foreground font-sans">/mo</span></p>
            <p className="text-[9px] text-muted-foreground mt-1">Browse & sell</p>
          </a>
          <a
            href={`${CREATOR_LINK}?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.email || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-violet-200 rounded-xl p-4 text-left hover:border-violet-400 transition-colors block bg-violet-50/30 relative"
          >
            <div className="absolute -top-2 right-3 bg-violet-600 text-white text-[8px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">Popular</div>
            <Sparkles size={14} className="text-violet-500 mb-1" />
            <p className="text-[10px] text-violet-600 font-semibold uppercase tracking-wider mb-1">Creator AI</p>
            <p className="font-serif text-xl text-obsidian">{'\u20AC'}49<span className="text-[10px] text-muted-foreground font-sans">/mo</span></p>
            <p className="text-[9px] text-muted-foreground mt-1">AI tools included</p>
          </a>
          <a
            href={`${STUDIO_LINK}?client_reference_id=${user?.id || ''}&prefilled_email=${encodeURIComponent(user?.email || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-rose-200 rounded-xl p-4 text-left hover:border-rose-400 transition-colors block bg-rose-50/30 relative"
          >
            <div className="absolute -top-2 right-3 bg-rose-600 text-white text-[8px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">Pro</div>
            <Zap size={14} className="text-rose-500 mb-1" />
            <p className="text-[10px] text-rose-600 font-semibold uppercase tracking-wider mb-1">AI Studio</p>
            <p className="font-serif text-xl text-obsidian">{'\u20AC'}100<span className="text-[10px] text-muted-foreground font-sans">/mo</span></p>
            <p className="text-[9px] text-muted-foreground mt-1">Full AI + video</p>
          </a>
        </div>

        {/* CTA */}
        <div className="space-y-3">
          <Link to="/pricing">
            <Button
              data-testid="subscription-wall-pricing-btn"
              className="w-full rounded-full h-12 bg-obsidian hover:bg-amber-600 transition-colors duration-300 font-medium text-sm"
            >
              <Crown size={18} className="mr-2" />
              View Plans & Subscribe
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>

          <p className="text-xs text-muted-foreground">
            Your products and profile are safe. Subscribe to regain full access.
          </p>
        </div>

        {/* What's included reminder */}
        <div className="mt-8 border-t border-border/30 pt-6">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">What you get</p>
          <div className="grid grid-cols-2 gap-2 text-left">
            {[
              'AI Product Creator',
              'Design Studio',
              'Marketplace listing',
              'Community access',
              'Creator Dashboard',
              'Print-on-demand',
            ].map((f) => (
              <div key={f} className="flex items-center gap-2 text-xs text-obsidian/70">
                <Check size={12} className="text-amber-500 shrink-0" />
                {f}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
